import {
  Dialog, DialogFooter, DialogType, mergeStyleSets, PrimaryButton, Text,
} from '@fluentui/react';
import React from 'react';
import Store from '../shared/state/Store';
import UiReducer from '../shared/state/UiReducer';
import Theme from '../shared/Theme';
import IconButton from '../shared/IconButton';
import AppsApi from '../shared/util/AppsApi';

const componentStyles = mergeStyleSets({
  tableWrapper: {
    maxHeight: '70vh',
    overflowY: 'auto',
    '-webkit-overflow-scrolling': 'touch',
    selectors: {
      thead: {
        position: 'sticky',
        top: 0,
        zIndex: 2,
        backgroundColor: 'white',
      },
    },
  },
});

export default () => {
  const [globalState, dispatch] = Store.useStore();

  const licenseKey = UiReducer.getGenericUiAttribute(globalState, UiReducer.Keys.LocalActivationsDialogOpen);

  const hide = () => {
    dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.LocalActivationsDialogOpen, undefined));
  };

  if (!licenseKey) {
    return <></>;
  }

  const {
    activations = [],
    totalActivations = 0,
    totalDeactivations = 0,
    allowedActivations = 0,
    allowedDeactivations = 0,
    key: licenseKeyValue,
  } = licenseKey;

  const handleDeactivate = async (activationId) => {
    if (totalDeactivations >= allowedDeactivations) {
      dispatch(UiReducer.uiSetErrors([
        'You have reached the maximum number of deactivations allowed.',
        'Please contact support@agena.ai to increase your limit.',
      ]));
      return;
    }
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to release this activation?\nThis will decrease the number of available deactivations.')) {
      return;
    }

    try {
      const response = await AppsApi.deleteKeyActivation({ key: licenseKeyValue }, activationId);
      if (response?.code >= 300 || response?.error?.code >= 300) {
        dispatch(UiReducer.uiSetErrors(response.messages));
        return;
      }

      // Get current account info and find the account that contains this key
      const accountInfo = UiReducer.getGenericUiAttribute(globalState, UiReducer.Keys.AccountInfo);
      if (accountInfo) {
        const updatedAccountInfo = { ...accountInfo };

        // Check if the key is in primaryAccount
        const keyInPrimary = updatedAccountInfo.primaryAccount?.keys?.some((k) => k.key === licenseKeyValue);
        let secondaryIndex = -1;
        if (keyInPrimary) {
          // Replace primaryAccount with the returned account object
          updatedAccountInfo.primaryAccount = response;
        } else {
          // Check secondaryAccounts
          secondaryIndex = updatedAccountInfo.secondaryAccounts?.findIndex((secondaryAccount) => secondaryAccount.keys?.some((k) => k.key === licenseKeyValue)) ?? -1;
          if (secondaryIndex >= 0) {
            // Replace the secondary account with the returned account object
            updatedAccountInfo.secondaryAccounts = [...updatedAccountInfo.secondaryAccounts];
            updatedAccountInfo.secondaryAccounts[secondaryIndex] = response;
          }
        }

        // Update the account info in the store
        dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.AccountInfo, updatedAccountInfo));

        // Find the updated key in the new accountInfo and update the dialog
        let updatedKey = null;
        if (keyInPrimary) {
          updatedKey = updatedAccountInfo.primaryAccount?.keys?.find((k) => k.key === licenseKeyValue);
        } else if (secondaryIndex >= 0) {
          const updatedSecondaryAccount = updatedAccountInfo.secondaryAccounts?.[secondaryIndex];
          updatedKey = updatedSecondaryAccount?.keys?.find((k) => k.key === licenseKeyValue);
        }

        if (updatedKey) {
          // Update the dialog with the updated key data
          dispatch(UiReducer.setGenericUiAttribute(UiReducer.Keys.LocalActivationsDialogOpen, updatedKey));
        }
      }
    } catch (error) {
      if (error instanceof Error) {
        dispatch(UiReducer.uiSetErrors([error.message]));
      } else {
        dispatch(UiReducer.uiSetErrors(['Failed to release activation']));
      }
    }
  };

  const iconButtonStyles = {
    icon: {
      fontSize: '20px !important',
      height: '20px !important',
      lineHeight: '20px !important',
    },
  };

  return (
    <div style={{
      position: 'fixed',
      left: '-9999px',
      width: '0px',
      height: '0px',
    }}
    >
      <Dialog
        hidden={false}
        onDismiss={hide}
        dialogContentProps={{
          type: DialogType.normal,
          title: 'Local Activations',
        }}
        modalProps={{
          isBlocking: false,
        }}
        styles={{
          main: {
            maxWidth: '750px !important',
          },
        }}
      >
        <Text block>
          Activations:
          {' '}
          {totalActivations}
          {' '}
          /
          {' '}
          {allowedActivations}
        </Text>
        <Text block style={{ marginBottom: '10px' }}>
          Deactivations:
          {' '}
          {totalDeactivations}
          {' '}
          /
          {' '}
          {allowedDeactivations}
        </Text>

        {activations.length === 0 ? (
          <Text block style={{ padding: '10px', color: '#605e5c' }}>No current activations</Text>
        ) : (
          <div className={componentStyles.tableWrapper}>
            <table
              className={[Theme.styles.table,
                Theme.styles.tableNoPadding,
                Theme.styles.tableText,
                Theme.styles.tableRowHighlight,
              ].join(' ')}
            >
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date Time</th>
                  <th>Hostname</th>
                  <th>OS</th>
                  <th>IP</th>
                  <th>Country</th>
                  <th>&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {activations.map((activation, index) => (
                  <tr key={activation.id}>
                    <td>{index + 1}</td>
                    <td>{activation.createdAt ? new Date(activation.createdAt).toLocaleString(navigator.languages[0]) : 'N/A'}</td>
                    <td>{activation.hostname || 'N/A'}</td>
                    <td>
                      {activation.os || 'N/A'}
                      {activation.osVersion ? ` ${activation.osVersion}` : ''}
                    </td>
                    <td>{activation.location?.ipAddress || 'N/A'}</td>
                    <td>{activation.location?.countryName || 'N/A'}</td>
                    <td>
                      <IconButton
                        iconName="Delete"
                        tooltip="Deactivate"
                        onClick={() => {
                          handleDeactivate(activation.id);
                        }}
                        styles={iconButtonStyles}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <PrimaryButton onClick={hide} text="Close" />
        </DialogFooter>
      </Dialog>
    </div>
  );
};
